import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Official-style WhatsApp glyph — kept lightweight so it can be reused in
// buttons across the app (Talent PMS, Warnings, Loan Account, Hiring, etc.).
export const WhatsAppIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 32 32" fill="currentColor" className={className} aria-hidden="true">
    <path d="M16 3C9.376 3 4 8.376 4 15c0 2.35.678 4.54 1.85 6.394L4 29l7.812-1.813A11.94 11.94 0 0 0 16 27c6.624 0 12-5.376 12-12S22.624 3 16 3zm0 21.75c-1.86 0-3.68-.51-5.264-1.474l-.377-.223-4.634 1.077 1.098-4.517-.244-.39A9.7 9.7 0 0 1 6.25 15c0-5.376 4.374-9.75 9.75-9.75s9.75 4.374 9.75 9.75-4.374 9.75-9.75 9.75zm5.55-7.28c-.302-.15-1.787-.882-2.064-.983-.276-.1-.478-.15-.68.15-.202.302-.782.983-.958 1.184-.176.202-.353.226-.655.076-.302-.151-1.276-.47-2.43-1.5a9.084 9.084 0 0 1-1.68-2.09c-.176-.302-.019-.464.132-.615.135-.135.302-.353.454-.53.15-.176.202-.302.302-.503.1-.202.05-.378-.025-.53-.076-.15-.68-1.64-.933-2.246-.246-.593-.496-.512-.68-.522-.176-.008-.378-.01-.58-.01-.202 0-.53.075-.807.378-.276.302-1.06 1.036-1.06 2.526 0 1.49 1.086 2.93 1.237 3.132.151.202 2.13 3.253 5.164 4.564.722.311 1.286.497 1.725.636.724.23 1.383.198 1.903.12.58-.087 1.787-.73 2.038-1.436.252-.706.252-1.31.176-1.436-.076-.126-.276-.202-.578-.353z" />
  </svg>
);

/**
 * Reusable "Send via WhatsApp" button.
 *
 * Props:
 *   template         — Meta template name (e.g. "salary_slip_share")
 *   toRaw            — recipient phone (E.164 or UAE mobile format)
 *   bodyParameters   — Meta body param array (position matters!)
 *   entityType       — audit tag ("salary_slip", "warning_letter", ...)
 *   entityId         — audit FK
 *   recipientName    — for the audit trail
 *   recipientType    — "rider" | "staff" | "lessee"
 *   recipientId      — internal id
 *   mediaUrl         — optional URL/path to a PDF to attach as document header
 *   size, variant, className, label — visual overrides
 *   confirmMessage   — optional confirm prompt before sending
 */
export const WhatsAppButton = ({
  template,
  toRaw,
  bodyParameters = [],
  entityType,
  entityId,
  recipientName,
  recipientType,
  recipientId,
  mediaUrl,
  size = 'sm',
  variant = 'outline',
  className = '',
  label = 'Send via WhatsApp',
  confirmMessage,
  onSent,
  disabled = false,
  'data-testid': testId,
}) => {
  const [sending, setSending] = useState(false);

  const doSend = async () => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    if (!toRaw) {
      toast.error('No recipient phone number on file');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/whatsapp/manual-send`,
        {
          template_name: template,
          to_raw: toRaw,
          body_parameters: bodyParameters,
          entity_type: entityType,
          entity_id: entityId,
          recipient_name: recipientName,
          recipient_type: recipientType,
          recipient_id: recipientId,
          media_url: mediaUrl || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const status = res.data?.status;
      if (status === 'sent' || status === 'delivered') {
        toast.success(`WhatsApp queued for ${recipientName || toRaw}`);
      } else if (status === 'skipped') {
        toast.warning(`WhatsApp skipped — ${res.data?.reason || 'recipient not opted in'}`);
      } else {
        toast.error(`WhatsApp failed — ${res.data?.error || 'unknown'}`);
      }
      onSent?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'WhatsApp send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Button
      size={size}
      variant={variant}
      onClick={doSend}
      disabled={disabled || sending || !toRaw}
      className={`gap-1.5 ${className}`}
      title={toRaw ? `Send ${template} via WhatsApp to ${toRaw}` : 'No phone number on file'}
      data-testid={testId || `whatsapp-${template}`}
    >
      <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
      {sending ? 'Sending…' : label}
    </Button>
  );
};

export default WhatsAppButton;
